import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, logisticsApi, formatCurrency, formatDate, statusLabels, statusColors } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, FileDown, Loader2, MapPin, User, Calendar as CalIcon, Tag } from "lucide-react";
import { toast } from "sonner";
import OrderLogisticsSection from "@/components/orders/OrderLogisticsSection";
import OrderDescriptionEditor from "@/components/orders/OrderDescriptionEditor";
import OrderMediaSection from "@/components/orders/OrderMediaSection";
import OrderMaterialsTemplateSection from "@/components/orders/OrderMaterialsTemplateSection";
import OrderMaterialsSection from "@/components/orders/OrderMaterialsSection";

/**
 * Page Fiche Commande complète (Logistique, Descriptif Rich Text, Médias,
 * Génération PDF Fiche de Chantier).
 */
export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchOrder = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.getOrder(id);
      setOrder(res.data);
    } catch (e) {
      toast.error("Commande introuvable");
      navigate("/commandes");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  const handleExportPdf = async () => {
    try {
      setExporting(true);
      const res = await logisticsApi.getOrderWorksheetPdf(id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const ref = (order?.reference || id.slice(0, 8)).replace(/\//g, "-");
      a.download = `Fiche_Chantier_${ref}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
      toast.success("Fiche de chantier générée");
    } catch (e) {
      toast.error("Erreur lors de la génération du PDF");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-blue-700" />
      </div>
    );
  }

  if (!order) return null;

  const status = order.is_on_hold ? "paused" : order.status;
  const statusLabel = order.is_on_hold ? "En pause" : (statusLabels?.[order.status] || order.status);
  const statusColor = order.is_on_hold ? "bg-amber-100 text-amber-800" : (statusColors?.[order.status] || "bg-slate-100 text-slate-800");

  return (
    <div className="space-y-5 animate-in" data-testid="order-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/commandes")}
            data-testid="order-detail-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Retour
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold flex items-center gap-2" data-testid="order-detail-title">
              Commande <span className="font-mono text-blue-700">{order.reference || id.slice(0, 8)}</span>
              <Badge className={statusColor} data-testid="order-detail-status">{statusLabel}</Badge>
            </h1>
            <p className="text-sm text-muted-foreground">
              {order.client_name || "Client inconnu"}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExportPdf} disabled={exporting} data-testid="order-detail-export-pdf-btn">
            {exporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileDown className="h-4 w-4 mr-1" />}
            Fiche de chantier PDF
          </Button>
        </div>
      </div>

      {/* Summary card */}
      <Card>
        <CardContent className="pt-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-start gap-2">
              <User className="h-4 w-4 text-blue-700 mt-0.5" />
              <div>
                <div className="text-xs text-muted-foreground">Client</div>
                <div className="font-medium" data-testid="order-detail-client">{order.client_name || "—"}</div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 text-blue-700 mt-0.5" />
              <div>
                <div className="text-xs text-muted-foreground">Ville</div>
                <div className="font-medium">{order.city || "—"}</div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CalIcon className="h-4 w-4 text-blue-700 mt-0.5" />
              <div>
                <div className="text-xs text-muted-foreground">Date de commande</div>
                <div className="font-medium">{order.order_date ? formatDate(order.order_date) : "—"}</div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Tag className="h-4 w-4 text-blue-700 mt-0.5" />
              <div>
                <div className="text-xs text-muted-foreground">Montant HT</div>
                <div className="font-medium">{order.amount_ht != null ? formatCurrency(order.amount_ht) : "—"}</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logistics */}
      <OrderLogisticsSection order={order} onOrderRefresh={fetchOrder} />

      {/* Materials (templates) */}
      <OrderMaterialsTemplateSection order={order} />

      {/* Materials (CRUD + PDF visibility — Phase 180) */}
      <OrderMaterialsSection order={order} />

      {/* Description (Rich Text) */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Descriptif (Rich Text)</CardTitle>
        </CardHeader>
        <CardContent>
          <OrderDescriptionEditor
            orderId={order.id}
            initialHtml={order.description_html || ""}
            onSaved={(html) => setOrder((o) => o ? { ...o, description_html: html } : o)}
          />
        </CardContent>
      </Card>

      {/* Media */}
      <OrderMediaSection orderId={order.id} />
    </div>
  );
}
